// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps, IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { RadioGroupProps } from "@insite/mobius/RadioGroup";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries, getProp, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CaretRight from "@werner/XDL/Icons/CaretRight";
import Edit from "@werner/XDL/Icons/Edit";
import { xScreen } from "@werner/XDL/Utilities";
import color from "@werner/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@werner/XDL/Utilities/links";
import { mergeWithCss } from "@werner/XDL/Utilities/mergeWithCss";
import { ellipsis } from "@werner/XDL/Utilities/string";
import styled, { css } from "styled-components";

export const hideElement = css`
    &&&& {
        display: none;
        @media print {
            display: none;
        }
    }
`;

export const hideElementOnPrint = css`
    &&&& {
        @media print {
            display: none;
        }
    }
`;

export const displayFlexOnPrint = css`
    &&&& {
        @media print {
            display: flex;
            margin-right: 0;
        }
    }
`;

export const avoidBreakInsidePrintElement = css`
    @media print {
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
    }
`;

export const styledLinksMenu = (width: number) => {
    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 0;
        padding: 0;
        ${StyledClickable} {
            display: flex;
            width: 100%;
            color: ${resolveColor("text", theme)};
            ${/* sc-selector */ StyledTypography} {
                font-size: 15px;
                letter-spacing: 0;
                line-height: 24px;
                padding: 16px;
                color: ${resolveColor("text", theme)} !important;
                text-transform: none;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor("common.ui2", theme)};
                color: ${resolveColor("text", theme)};
                text-decoration: none;
            }
        }
    `;
};

export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        -moz-appearance: textfield;
    }
`;

export const layoutContainer = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { paddingValues = [20] }: { paddingValues: number[] } = theme.breakpoints;

        const containerStyles = maxWidths.map(
            (maxWidth, index) =>
                css`
                    &:not(.full-width) {
                        max-width: ${maxWidth}px;
                        padding-left: ${paddingValues[index] || paddingValues[0]}px;
                        padding-right: ${paddingValues[index] || paddingValues[0]}px;
                        margin-right: auto;
                        margin-left: auto;
                    }
                    &.mobile-full-width,
                    &.full-width {
                        max-width: 100%;
                        margin-right: 0;
                        margin-left: 0;
                        padding-left: 0;
                        padding-right: 0;
                    }
                `,
        );
        return css`
            ${breakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

export const companyLogoItem: any = {
    css: css`
        display: none;
        padding: 0;
        width: 125px;
        float: left; // for print only

        @media print {
            display: block;
            padding-bottom: 20px;
            padding-left: 25px;
            margin-top: 5px;
        }

        img {
            display: block;
            max-width: 100%;
            height: auto;
        }
    `,
};

export const createLinkIconStyles = (icon: React.ComponentType | string, linkStyles?: LinkPresentationProps) => {
    const marginPosition = linkStyles?.icon?.position === "right" ? "left" : "right";
    return {
        ...mergeWithCss(
            {
                typographyProps: {
                    size: 12,
                    weight: 500,
                    css: css`
                        letter-spacing: 0;
                        line-height: 18px;
                    `,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: 14,
                        color: "common.ui3",
                        css: css`
                            margin-${marginPosition}: 8px;
                        `,
                    },
                    position: "left",
                },
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h1",
    size: 32,
    weight: 900,
    css: css`
        line-height: 40px;
        margin-bottom: 20px;
        display: block;
    `,
};

export const generateButtonLinkStyle = (iconSrc: React.ComponentType | string) => {
    return {
        ...mergeWithCss(
            iconButtonCommonStyles(iconSrc, 12, {
                typographyProps: {
                    size: 12,
                    weight: "bold",
                },
            }),
            {
                css: css`
                    margin: 0 10px;
                `,
            },
        ),
    };
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        css: css`
            flex-direction: row-reverse;
            justify-content: space-between;
            background-color: ${color("primary", 0.05)};
            padding: 20px 16px 20px 30px;
            max-width: unset;
            width: calc(100% + 16px);
            flex-basis: auto;
            display: flex;
            &&& {
                margin: 35px -16px 0;
            }

            & > button {
                width: calc(50% - 10px);
                min-width: unset;
            }

            ${xScreen("lte", "xs")} {
                & > button {
                    width: 100%;
                }
                flex-direction: column-reverse;
            }
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-wrap: wrap;
            justify-content: space-between;
            padding: 20px 40px;
            background: ${color("primary", 0.05)};
        `,
    },
    cancelButton: {
        color: "transparent",
        css: css`
            margin-right: 10px;
            border: none;
            color: ${color("common.ui3")};
            justify-content: center;

            ${IconWrapper} {
                top: 0;
            }

            svg {
                color: ${color("common.ui3")};
                fill: ${color("common.ui3")};
                height: 10px;
                width: 10px;
            }

            &:hover,
            &:active,
            &:focus {
                background-color: transparent;
                color: ${color("common.ui3")};
                box-shadow: none;
            }

            ${xScreen("lte", "xs")} {
                margin-top: 10px;
            }
        `,
    },
    submitButton: {
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                order: 0;
            }
        `,
    },
};

export const StyledBox = styled(StyledWrapper)`
    width: 100%;
    padding: 20px 0;
    display: flex;
    flex-direction: column;
    border: 1px solid ${color("primary", 0.05)};
    background-color: ${color("common.white")};
    box-shadow: 0 0 15px 0 ${color("primary", 0.15)};
    ${avoidBreakInsidePrintElement}
`;

export const sharedBreadcrumbsPageStyles = css`
    &&&& {
        padding-top: 16px;
        padding-bottom: 20px;
    }
`;

export const sharedBadgesContainerStyles = css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

interface FulfillmentSelectorStyles {
    container?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressGridItem?: GridItemProps;
    radioGroup?: RadioGroupProps;
    addressHeaderWrapper?: InjectableCss;
    locationHeaderText?: TypographyPresentationProps;
    locationChangeLink?: LinkPresentationProps;
}

export const fulfillmentSelectorStyles: FulfillmentSelectorStyles = {
    container: {
        gap: 0,
        css: css`
            width: 100%;
            background: ${color("primary", 0.05)};
            padding: 24px 30px 30px 30px;
            min-height: 160px;
        `,
    },
    fulfillmentMethodGridItem: {
        width: [12, 12, 5, 5, 5],
        css: css`
            ${xScreen("lte", "sm")} {
                padding-bottom: 38px;
            }
        `,
    },
    addressGridItem: {
        width: [12, 12, 7, 7, 7],
        css: css`
            &:has(#addressDrawerPickUpLocationSelector:empty) {
                border-left: none;
            }
            padding-left: 38px;
            border-left: 1px solid ${color("common.ui1")};
            ${xScreen("lte", "sm")} {
                border-left: none;
                border-top: 1px solid ${color("common.ui1")};
                padding-left: 0;
                padding-top: 38px;
            }
        `,
    },
    radioGroup: {
        horizontal: true,
        labelProps: {
            as: "p",
            size: 24,
            weight: 500,
            transform: "capitalize",
            css: css`
                letter-spacing: 0;
                line-height: 27px;
            `,
        } as TypographyProps,
        css: css`
            & > ${TypographyStyle as any} {
                padding: 0;
                margin-bottom: 18px;
            }
        `,
    },
    addressHeaderWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
        `,
    },
    locationHeaderText: {
        weight: 500,
        size: 15,
        css: css`
            letter-spacing: 0;
            line-height: 20px;
            &:after {
                content: ":";
            }
        `,
    },
    locationChangeLink: {
        ...createLinkIconStyles(Edit, {
            color: "text.main",
            icon: {
                position: "right",
            },
            typographyProps: {
                color: "text.main",
            },
        }),
    },
};

export const sharedProductDescriptionStyles: ProductDescriptionStyles = {
    productDetailDescription: {
        size: 16,
        color: "common.ui3",
        weight: 400,
        css: css`
            letter-spacing: 0;
            line-height: 22px;
            ${ellipsis(2)}
        `,
    },
    productDetailLink: {
        color: "secondary",
        typographyProps: {
            size: 16,
            color: "common.ui3",
            weight: 400,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
                ${ellipsis(2)}
            `,
        },
    },
};

export const sharedTextBadgeStyle: InjectableCss = {
    css: css`
        max-width: 86px;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0;
        min-height: 18px;
        border-radius: 14px;
        background: ${color("common.white")};
        color: ${color("info")};
        border: 1px solid;
        & ${TypographyStyle} {
            font-size: 10px;
            letter-spacing: 0.55px;
            line-height: 12px;
            weight: 400;
            text-transform: uppercase;
        }
    `,
};

export const previouslyPurchasedBadgeStyles: InjectableCss = {
    ...mergeWithCss(sharedTextBadgeStyle, {
        css: css`
            width: 86px;
            max-width: unset;
            margin-top: 5px;
        `,
    }),
};

export const BreadcrumbsActionsWrapper = styled(StyledWrapper)`
    display: flex;
    gap: 10px 45px;
    flex-wrap: wrap;
    align-items: baseline;
`;

const sharedBreadCrumbActionStyles: LinkPresentationProps = {
    color: "secondary",
    typographyProps: {
        size: 15,
        weight: 500,
        css: css`
            letter-spacing: 0;
            line-height: 18px;
        `,
    },
    icon: {
        iconProps: {
            size: 20,
        },
    },
};

export const myAccountLinkStyles: LinkPresentationProps = {
    color: "secondary",
    typographyProps: {
        size: 15,
        weight: 500,
        css: css`
            line-height: 30px;
            letter-spacing: 0;
            ${ellipsis(1)}
        `,
    },
};

export const createBreadcrumbActionLink = (icon: React.ComponentType | string, linkStyles?: LinkPresentationProps) => {
    return createLinkIconStyles(icon, mergeWithCss(sharedBreadCrumbActionStyles, linkStyles));
};

/**
 *
 * @param rules css rules
 * @returns media query to match safari 10.1+ browsers
 */
export const xSafariOnly = (rules: string) => {
    return `@media not all and (min-resolution:.001dpcm) {
                @supports (-webkit-appearance:none) {
                   ${rules}
                }
            }`;
};

/**
 *
 * @param rules css rules
 * @returns firefox only css styles
 */
export const xFirefoxOnly = (rules: string) => {
    return `@-moz-document url-prefix() { 
        ${rules}
      }`;
};
